(ns ui.editors.simple-filters.view
  (:require
   [clojure.set :as set]
   [clojure.string :as string]
   [common.currencies :as currencies]
   [common.fields.filter-types :as filter-types]
   [common.localization :as loc]
   [common.schema.data-types :as data-types]
   [re-frame.core :as rf]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.date-picker.view :as date-picker]
   [ui.components.date-range-picker.view :as date-range-picker]
   [ui.components.input.search :as search]
   [ui.components.input.simple-text :as simple-text-input]
   [ui.components.layout.grid :as grid-layout]
   [ui.components.menu.view :as menu]
   [ui.components.radix-ui.popover.view :as popover]
   [ui.dom-globals :as dg]
   [ui.editors.simple-filters.commit :as commit]
   [ui.editors.simple-filters.components.selection-row :as operator-row]
   [ui.editors.simple-filters.event :as event]
   [ui.editors.simple-filters.model :as model]
   [ui.editors.simple-filters.sub :as sub]))

(def known-option-value :filter.option/known)
(def unknown-option-value :filter.option/unknown)

(def op-known :operand/known)
(def op-unknown :operand/unknown)
(def op-eq :operand/equals)
(def op-any-of :operand/any-of)
(def op-is-not :operand/none-of)
(def op-lt :operand/smaller-than)
(def op-gt :operand/greater-than)
(def op-between :operand/between-inclusive)
(def op-date-eq :operand/equals-date)
(def op-date-lt :operand/on-or-before)
(def op-date-gt :operand/on-or-after)
(def op-date-between :operand/between-inclusive-date)
(def op-contains :operand/contains)
(def op-starts-with :operand/starts-with-display-string)
(def op-ends-with :operand/ends-with-display-string)
(def op-true :operand/true)
(def op-false :operand/false)

(def unary-operators
  #{op-known op-unknown op-true op-false})

(def default-currency-value :eur)
(def date-preset-custom :date.preset/custom)
(def numeric-preset-namespace "numeric.percentile-range")
(def no-suggestion-text-data-types
  #{:data.type/text-richtext
    :data.type/text-multiline
    :data.type/text-edn})

(def field-search-dropdown-max-height 320)
(def field-search-dropdown-offset 8)

(defn- normalize-currency-value
  [currency-value]
  (cond
    (keyword? currency-value)
    currency-value

    (string? currency-value)
    (let [kw (keyword (string/lower-case currency-value))]
      (if (contains? currencies/value-options-by-value kw)
        kw
        default-currency-value))

    :else
    default-currency-value))

(defn- field-operand-type
  [field]
  (let [data-type (:field/data-type field)
        filter-type (or (:field/filter-type field)
                        (get-in data-types/zapflow-data-types [data-type :default-filter]))
        operand-type (get filter-types/operand-types-by-filter-type filter-type)]
    (or operand-type :text)))

(defn- field-label
  [field-path field]
  (or (:field/label field)
      (:field/name field)
      (:label field)
      (name (first field-path))))

(defn- contains-operator-label
  [field]
  (if (= (:field/data-type field) :data.type/file)
    "File name contains"
    (loc/tr :filters-editor/operator-contains)))

(defn- supports-text-suggestions?
  [field]
  (let [operand-type (field-operand-type field)
        data-type (:field/data-type field)]
    (and (= operand-type :text)
         (not (contains? no-suggestion-text-data-types data-type)))))

(defn- selection->id
  [selection]
  (or (:id selection)
      (pr-str (dissoc selection :id))))

(defn- selection->dom-id
  [selection]
  (let [selection-id (selection->id selection)
        hashed-id (-> selection-id hash abs)]
    (str "simple-filter-option-" hashed-id)))

(defn- active-selection?
  [selection]
  (let [value (:value selection)
        from (:from selection)
        to (:to selection)
        meaningful-value? (fn [v]
                            (cond
                              (nil? v) false
                              (string? v) (not (string/blank? v))
                              (coll? v) (seq v)
                              :else true))]
    (boolean
     (or (meaningful-value? value)
         (and (meaningful-value? from)
              (meaningful-value? to))
         (contains? unary-operators (:operator selection))))))

(defn- set-field-search-dropdown-direction!
  [field-search-input-ref* field-search-dropdown-direction*]
  (if-let [input-el @field-search-input-ref*]
    (let [rect (dg/bounding-client-rect input-el)
          viewport-height (or (dg/window-inner-height) 0)
          space-above (max 0 (or (dg/rect-top rect) 0))
          space-below (max 0 (- viewport-height (or (dg/rect-bottom rect) 0)))
          required-space (+ field-search-dropdown-max-height field-search-dropdown-offset)
          open-up? (or (>= space-above 180)
                       (and (< space-below required-space)
                            (> space-above space-below)))]
      (reset! field-search-dropdown-direction* (if open-up? :up :down)))
    (reset! field-search-dropdown-direction* :down)))

(defn- comparable-value-forms
  [value]
  (cond
    (keyword? value)
    (let [kw-str (subs (str value) 1)
          kw-name (name value)
          kw-ns (namespace value)]
      (cond-> #{(str value) kw-str kw-name}
        kw-ns (conj (str kw-ns "/" kw-name))))

    (string? value)
    #{value
      (str ":" value)}

    :else
    #{(str value)}))

(defn- values-equivalent?
  [a b]
  (or (= a b)
      (boolean
       (seq (set/intersection (comparable-value-forms a)
                              (comparable-value-forms b))))))

(defn- raw-option->value
  [option]
  (cond
    (contains? option :value) (:value option)
    (contains? option :key) (:key option)
    (contains? option :keyword) (:keyword option)
    :else nil))

(defn- raw-option->label
  [option value]
  (or (:label option)
      (when (keyword? value) (name value))
      (str value)))

(defn- field-options
  [field]
  (->> (or (:field/options field) (:options field) [])
       (keep (fn [option]
               (let [value (raw-option->value option)]
                 (when (some? value)
                   {:value value
                    :label (raw-option->label option value)
                    :count (:count option)}))))
       vec))

(defn- existence-options
  [facet-options]
  (let [by-value (->> (or facet-options [])
                      (map (fn [option]
                             [(:value option) option]))
                      (into {}))
        known-option (get by-value known-option-value)
        unknown-option (get by-value unknown-option-value)]
    [{:value known-option-value
      :label (or (:label known-option) (loc/tr :filters-editor/is-known))
      :count (:count known-option)}
     {:value unknown-option-value
      :label (or (:label unknown-option) (loc/tr :filters-editor/is-unknown))
      :count (:count unknown-option)}]))

(declare numeric-preset-option?
         numeric-preset->row)

(defn- operator-options
  [field operand-type facet-options]
  (let [options-by-value (->> (or facet-options [])
                              (map (fn [option]
                                     [(:value option) option]))
                              (into {}))
        [known-option unknown-option] (existence-options facet-options)
        true-option (get options-by-value true)
        false-option (get options-by-value false)
        known-row {:op op-known
                   :label (or (:label known-option) (loc/tr :filters-editor/is-known))
                   :count (:count known-option)}
        unknown-row {:op op-unknown
                     :label (or (:label unknown-option) (loc/tr :filters-editor/is-unknown))
                     :count (:count unknown-option)}]
    (case operand-type
      :on-off
      [{:op op-true
        :label (loc/tr :filters-editor/is-true)
        :count (:count true-option)}
       {:op op-false
        :label (loc/tr :filters-editor/is-false)
        :count (:count false-option)}
       known-row
       unknown-row]

      :numeric
      (vec (concat
            [known-row
             unknown-row]
            (->> (or facet-options [])
                 (filter numeric-preset-option?)
                 (map numeric-preset->row)
                 (remove nil?))
            [{:op op-eq :label (loc/tr :filters-editor/operator-equal-to)}
             {:op op-lt :label (loc/tr :filters-editor/operator-less-than)}
             {:op op-gt :label (loc/tr :filters-editor/operator-greater-than)}
             {:op op-between :label (loc/tr :filters-editor/operator-between)}]))

      :date
      []

      :text
      [known-row
       unknown-row
       {:op op-contains
        :label (contains-operator-label field)}]

      [])))

(defn- date-operator-options
  [selected-field-path selected-field facet-options]
  (let [field-name (or (some-> (field-label selected-field-path selected-field) string/lower-case)
                       "date")
        options-by-value (->> (or facet-options [])
                              (map (fn [option]
                                     [(:value option) option]))
                              (into {}))
        known-option (get options-by-value known-option-value)
        unknown-option (get options-by-value unknown-option-value)
        preset-options (->> (or facet-options [])
                            (filter (fn [{:keys [value]}]
                                      (and (keyword? value)
                                           (= "date.preset" (namespace value)))))
                            (map (fn [{:keys [value label count from to]}]
                                   {:op op-date-between
                                    :preset value
                                    :label (or label (name value))
                                    :count count
                                    :from from
                                    :to to}))
                            vec)]
    (vec (concat
          [{:op op-known
            :label (or (:label known-option) (str "Known " field-name))
            :count (:count known-option)}
           {:op op-unknown
            :label (or (:label unknown-option) (str "Unknown " field-name))
            :count (:count unknown-option)}
           {:op op-date-between :label "Custom range" :preset date-preset-custom}
           {:op op-date-lt :label "Before specific date"}
           {:op op-date-gt :label "After specific date"}]
          preset-options))))

(defn- derive-op-state
  [field-filter]
  (let [selection (first (or (:selections field-filter) []))
        value (:value selection)
        operator (:operator selection)
        currency (:currency selection)
        preset (:preset selection)]
    (cond
      (= value known-option-value) {:op op-known}
      (= value unknown-option-value) {:op op-unknown}
      (= value true) {:op op-true}
      (= value false) {:op op-false}
      (= operator op-is-not) {:op    op-is-not
                              :value value}
      (#{op-between op-date-between} operator) {:op     operator
                                                :from   (:from selection)
                                                :to     (:to selection)
                                                :preset preset}
      operator {:op       operator
                :value    value
                :currency currency
                :preset   preset}
      (some? value) {:op    op-contains
                     :value value}
      :else {})))

(defn- set-single-selection!
  [editor-id field-path selection]
  (rf/dispatch [::event/set-field-filter
                editor-id
                field-path
                {:field-path field-path
                 :mode       :single
                 :selections [(assoc selection :id (selection->id selection))]}]))

(defn- clear-local-op-state!
  [state* field-path]
  (when (and state* field-path)
    (swap! state* assoc field-path {})))

(defonce ^:private pending-commits*
  (atom {}))

(defn- cancel-pending-commit!
  [commit-key]
  (when-let [handle (get @pending-commits* commit-key)]
    (dg/clear-timeout! handle)
    (swap! pending-commits* dissoc commit-key)))

(defn- commit-now!
  "Apply the typed value now, and drop a commit that waits for a pause in typing."
  [commit-key commit!]
  (cancel-pending-commit! commit-key)
  (commit!))

(defn- commit-after-pause!
  "Apply the typed value when typing stops. Each call restarts the wait. The
   function `commit!` reads the local state when it runs, so it applies what the
   user typed last."
  [commit-key commit!]
  (cancel-pending-commit! commit-key)
  (swap! pending-commits* assoc commit-key
         (dg/set-timeout! (fn []
                            (swap! pending-commits* dissoc commit-key)
                            (commit!))
                          commit/debounce-ms)))

(defn- commit-scalar!
  "Apply the value of an input with one value, from the local state of the
   operator panel."
  [{:keys [editor-id selected-field-path currency-field? state*]}]
  (let [op-state  (get @state* selected-field-path)
        selection (commit/scalar-selection
                   (assoc op-state :currency (normalize-currency-value (:currency op-state)))
                   currency-field?)]
    (when (and selected-field-path selection)
      (set-single-selection! editor-id selected-field-path selection))))

(defn- commit-between!
  "Apply the values of a between input, when both values are there."
  [{:keys [editor-id selected-field-path state*]}]
  (let [selection (commit/between-selection (get @state* selected-field-path))]
    (when (and selected-field-path selection)
      (set-single-selection! editor-id selected-field-path selection))))

(defn- date-scalar-op?
  [op]
  (contains? #{op-date-eq op-date-lt op-date-gt} op))

(defn- selection->scalar-date-value
  [op selection]
  (let [value (:value selection)]
    (cond
      (number? value) value
      (and (= op op-date-gt) (number? (:from selection))) (:from selection)
      (and (= op op-date-lt) (number? (:to selection))) (:to selection)
      :else nil)))

(defn- parse-dd-mm-yyyy->utc-ts
  [value]
  (let [s (string/trim (or value ""))]
    (when-let [[_ dd mm yyyy] (re-matches #"(\d{1,2})\.(\d{1,2})\.(\d{4})" s)]
      (let [day (js/parseInt dd 10)
            month (js/parseInt mm 10)
            year (js/parseInt yyyy 10)]
        (when (and (<= 1 day 31)
                   (<= 1 month 12)
                   (<= 1900 year 3000))
          (js/Date.UTC year (dec month) day 0 0 0 0))))))

(defn- visible-date-input-value
  []
  (when-let [input-el (dg/query-selector (dg/body) "input[placeholder='dd.MM.yyyy']")]
    (dg/dom-value input-el)))

(defn- apply-date-preset!
  [editor-id field-path {:keys [preset label from to]}]
  (when (and (number? from) (number? to))
    (set-single-selection! editor-id field-path
                           {:kind     :value
                            :operator op-date-between
                            :preset   preset
                            :from     from
                            :to       to
                            :value    (str from " .. " to)
                            :label    label})))

(defn- numeric-preset-option?
  [{:keys [value from to label]}]
  (or (and (keyword? value)
           (= numeric-preset-namespace (namespace value)))
      (and (or (some? from) (some? to))
           (string? label))))

(defn- numeric-preset->row
  [{:keys [value label count from to]}]
  (cond
    (and (some? from) (some? to))
    {:op op-between
     :preset value
     :label label
     :count count
     :from from
     :to to}

    (some? to)
    {:op op-lt
     :preset value
     :label label
     :count count
     :to to}

    (some? from)
    {:op op-gt
     :preset value
     :label label
     :count count
     :from from}

    :else nil))

(defn- apply-numeric-preset!
  [editor-id field-path {:keys [preset label from to]}]
  (cond
    (and (some? from) (some? to))
    (set-single-selection! editor-id field-path
                           {:kind     :value
                            :operator op-between
                            :preset   preset
                            :from     from
                            :to       to
                            :value    (str from " .. " to)
                            :label    label})

    (some? to)
    (set-single-selection! editor-id field-path
                           {:kind     :value
                            :operator op-lt
                            :preset   preset
                            :value    to
                            :label    label})

    (some? from)
    (set-single-selection! editor-id field-path
                           {:kind     :value
                            :operator op-gt
                            :preset   preset
                            :value    from
                            :label    label})))

(defn- operator-row-selected?
  [current-op-state {:keys [op preset]}]
  (and (= op (:op current-op-state))
       (= preset (:preset current-op-state))))

(defn- select-option-row
  [{:keys [editor-id field-path selected-selections selected-operators operator option current-selections multi? control-type on-select]}]
  (let [{:keys [value label]} option
        operator (cond
                   (= value known-option-value) op-known
                   (= value unknown-option-value) op-unknown
                   :else operator)
        count-value (:count option)
        label-with-count (if (some? count-value)
                           (str label " (" count-value ")")
                           label)
        selection {:kind     :value
                   :operator operator
                   :value    value
                   :label    label}
        selection-id (selection->id selection)
        row-id (selection->dom-id selection)
        selected-by-value? (boolean
                            (some (fn [selected-selection]
                                    (values-equivalent? (:value selected-selection) value))
                                  (or selected-selections [])))
        selected? (cond
                    (= value known-option-value)
                    (or selected-by-value?
                        (contains? (or selected-operators #{}) op-known))

                    (= value unknown-option-value)
                    (or selected-by-value?
                        (contains? (or selected-operators #{}) op-unknown))

                    :else
                    selected-by-value?)]
    [operator-row/view
     {:selected?    selected?
      :id           row-id
      :control-type (or control-type
                        (if multi? :checkbox :radio))
      :label        label-with-count
      :on-click     (fn [_e]
                      (if multi?
                        (let [existing (or current-selections [])
                              without-known-unknown (->> existing
                                                         (remove (fn [{existing-value :value}]
                                                                   (or (= existing-value known-option-value)
                                                                       (= existing-value unknown-option-value))))
                                                         vec)
                              next-selections (if selected?
                                                (->> without-known-unknown
                                                     (remove (fn [existing-selection]
                                                               (and (= (:operator existing-selection) operator)
                                                                    (values-equivalent? (:value existing-selection) value))))
                                                     vec)
                                                (conj without-known-unknown (assoc selection :id selection-id)))]
                          (if (empty? next-selections)
                            (rf/dispatch [::event/clear-field-filter editor-id field-path])
                            (rf/dispatch [::event/set-field-filter
                                          editor-id
                                          field-path
                                          {:field-path field-path
                                           :mode       :multi
                                           :selections next-selections}])))
                        (if selected?
                          (rf/dispatch [::event/clear-field-filter editor-id field-path])
                          (set-single-selection! editor-id field-path selection)))
                      (when (fn? on-select)
                        (on-select option selection)))}]))

(defn- add-field-dropdown
  [{:keys [editor-id
           available-to-add
           field-by-path
           field-search-focused?*
           field-search-input-ref*
           field-search-dropdown-direction*
           selected-field-path]}]
  (let [position-class (if (= @field-search-dropdown-direction* :up)
                         "bottom-[calc(100%+8px)]"
                         "top-[calc(100%+8px)]")]
    [:div
     {:class (str "absolute left-0 right-0 "
                  position-class
                  " z-40 bg-white border tw2-border-dropdown-border-default rounded-[10px] shadow-sm overflow-hidden")}
     [:div
      {:class "max-h-[320px] overflow-y-auto py-1"}
      (if (empty? available-to-add)
        [:div
         {:class "px-3 py-2 text-[13px] tw2-text-tertiary"}
         (loc/tr :filters-editor/no-more-fields)]
        (doall
         (for [field-path available-to-add]
           ^{:key (str "search-hit-" (pr-str field-path))}
           [:div
            {:class         "h-[40px] px-3 flex items-center cursor-pointer hover:tw2-bg-field-bg-hover"
             :on-mouse-down (fn [e]
                              (dg/prevent-default e)
                              (rf/dispatch [::event/add-runtime-field-path editor-id field-path])
                              (rf/dispatch [::event/open-field-path editor-id field-path])
                              (rf/dispatch [::event/set-field-picker-search-text editor-id ""])
                              (when selected-field-path
                                (rf/dispatch [::event/clear-option-search-text editor-id selected-field-path]))
                              (reset! field-search-focused?* false)
                              (when-let [input-el @field-search-input-ref*]
                                (dg/blur! input-el)))}
            [:span
             {:class "text-[14px] leading-[20px] tw2-text-primary truncate"}
             (field-label field-path (get field-by-path field-path))]])))]]))

(defn- left-top-panel
  [{:keys [editor-id
           section-groups
           field-by-path
           selected-field-path
           filters-by-field-path]}]
  (let [menu-items (->> section-groups
                        (mapcat :field-paths)
                        distinct
                        (map (fn [field-path]
                               (let [field (get field-by-path field-path)
                                     selected-count (->> (get-in filters-by-field-path [field-path :selections] [])
                                                         (filter active-selection?)
                                                         count)]
                                 {:id       (pr-str field-path)
                                  :label    (field-label field-path field)
                                  :active   (= field-path selected-field-path)
                                  :on-click (fn [_e]
                                              (js/console.log "[simple-filters] left-panel field click"
                                                              #js {:editorId          editor-id
                                                                   :fieldPath         (pr-str field-path)
                                                                   :selectedFieldPath (pr-str selected-field-path)
                                                                   :active            (= field-path selected-field-path)})
                                              (rf/dispatch [::event/open-field-path editor-id field-path]))
                                  :end-icon [:span {:class "flex items-center gap-1"}
                                             [:span {:class "inline-flex min-w-[20px] h-[20px] items-center justify-center px-[6px] rounded-[6px] text-[12px] leading-[16px] font-medium bg-success-light text-success-light-text"
                                                     :style {:visibility (if (pos? selected-count) "visible" "hidden")}}
                                              (if (pos? selected-count) selected-count "0")]]})))
                        vec)]
    [:div
     {:class "flex h-full min-h-0 flex-col min-w-[200px]"}
     [:div
      {:class "flex-1 min-h-0 overflow-y-auto"}
      [menu/view
       {:items      menu-items
        :class      "tw2-p-filterbox"
        :aria-label "Filter categories"}]]]))

(defn- left-bottom-panel
  [{:keys [editor-id
           field-picker-search-text
           available-to-add
           field-by-path
           selected-field-path
           field-search-focused?*
           field-search-dropdown-direction*
           field-search-input-ref*]}]
  [:div
   {:class "border-t border-solid tw2-p-filterbox relative"
    :style {:border-color "var(--filterbox-border)"}}
   [:div
    {:class         "h-[40px] px-3 border tw2-border-field-border-default rounded-[10px] bg-white flex items-center gap-2 cursor-pointer"
     :on-mouse-down (fn [e]
                      (let [input-el @field-search-input-ref*
                            target-el (dg/target e)]
                        (when (and input-el
                                   (not= target-el input-el))
                          (dg/prevent-default e)
                          (set-field-search-dropdown-direction! field-search-input-ref* field-search-dropdown-direction*)
                          (reset! field-search-focused?* true)
                          (dg/focus! input-el))))}
    [:i.fal.fa-search {:class "text-[16px] tw2-text-tertiary"}]
    [simple-text-input/view
     {:ref         (fn [el]
                     (reset! field-search-input-ref* el))
      :class       (str "flex-1 min-w-0 w-0 border-0 bg-transparent px-0 py-0 h-[20px] text-[14px] leading-[20px] font-medium tw2-text-primary placeholder:tw2-text-field-placeholder-text "
                        (if (and (string/blank? field-picker-search-text)
                                 (not @field-search-focused?*))
                          "cursor-pointer"
                          "cursor-text"))
      :placeholder (loc/tr :filters-editor/search-more-filters)
      :value       field-picker-search-text
      :on-focus    (fn []
                     (set-field-search-dropdown-direction! field-search-input-ref* field-search-dropdown-direction*)
                     (reset! field-search-focused?* true))
      :on-blur     (fn [_value]
                     (js/setTimeout
                      (fn []
                        (reset! field-search-focused?* false))
                      0))
      :on-change   (fn [value]
                     (reset! field-search-focused?* true)
                     (rf/dispatch [::event/set-field-picker-search-text editor-id value]))}]
    [:i.fal
     {:class (str (if @field-search-focused?* "fa-angle-up" "fa-angle-down")
                  " text-[14px] tw2-text-tertiary pointer-events-none")}]]
   (when @field-search-focused?*
     [add-field-dropdown
      {:editor-id               editor-id
       :available-to-add        available-to-add
       :field-by-path           field-by-path
       :field-search-focused?*  field-search-focused?*
       :field-search-dropdown-direction* field-search-dropdown-direction*
       :field-search-input-ref* field-search-input-ref*
       :selected-field-path     selected-field-path}])])

(declare select-panel operator-panel)

(defn- right-top-panel
  [{:keys [editor-id selected-field selected-field-path selected-selections option-search-text current-op-state operand-type state* facet-options date-range-modal* facets-loading? facets-has-more?]}]
  [:div {:class "h-full min-h-0 tw2-p-filterbox"
         :style {:padding-right "0px"}}
   (if (= operand-type :option)
     [select-panel {:editor-id           editor-id
                    :selected-field      selected-field
                    :selected-field-path selected-field-path
                    :selected-selections selected-selections
                    :option-search-text  option-search-text
                    :current-op-state    current-op-state
                    :facet-options       facet-options
                    :facets-loading?     facets-loading?
                    :facets-has-more?    facets-has-more?}]
     [:div {:class "h-full min-h-0 overflow-y-auto overflow-x-hidden"
            :style {:scrollbar-gutter "stable"}}
      [:div {:class "tw2-pr-3 space-y-2"}
       [operator-panel {:editor-id           editor-id
                        :selected-selections selected-selections
                        :selected-field      selected-field
                        :selected-field-path selected-field-path
                        :operand-type        operand-type
                        :option-search-text  option-search-text
                        :current-op-state    current-op-state
                        :state*              state*
                        :facet-options       facet-options
                        :facets-loading?     facets-loading?
                        :date-range-modal*   date-range-modal*}]]])])

(defn- right-bottom-panel
  [{:keys [editor-id selected-field-path state*]}]
  [:div
   {:class "min-h-[51px] flex items-center tw2-p-filterbox"}
   [button/secondary-open
    {:class    "inline-flex items-center h-[20px] p-0 text-[14px] leading-[20px] font-medium tw2-text-secondary underline"
     :on-click (fn [_e]
                 (clear-local-op-state! state* selected-field-path)
                 (rf/dispatch [::event/clear-field-selection editor-id selected-field-path]))}
    (loc/tr :clear-selection)]])

(defn- select-panel
  [{:keys [editor-id selected-field selected-field-path selected-selections option-search-text current-op-state facet-options facets-loading? facets-has-more?]}]
  (let [q (string/lower-case (or option-search-text ""))
        selected-operators (->> (or selected-selections [])
                                (map :operator)
                                (remove nil?)
                                set)
        operator-options (->> (existence-options facet-options)
                              (filter (fn [{:keys [label]}]
                                        (or (string/blank? q)
                                            (-> label string/lower-case (string/includes? q)))))
                              vec)
        base-options (if (seq facet-options)
                       (->> facet-options
                            (remove (fn [{:keys [value]}]
                                      (or (= value known-option-value)
                                          (= value unknown-option-value))))
                            vec)
                       (->> (field-options selected-field)
                            (filter (fn [{:keys [label]}]
                                      (or (string/blank? q)
                                          (-> label string/lower-case (string/includes? q)))))
                            vec))
        select-op (if (= (:op current-op-state) op-is-not) op-is-not op-any-of)]
    [:div {:class "h-full min-h-0 flex flex-col"}
     [:div {:class "mb-4 shrink-0 tw2-pr-3"}
      [search/view
       {:placeholder   (loc/tr :search)
        :value         option-search-text
        :wrapper-class "tw2-flex-none tw2-w-full"
        :on-change     (fn [value]
                         (rf/dispatch [::event/set-option-search-text editor-id selected-field-path value]))}]]
     [:div {:class     "flex-1 min-h-0 overflow-y-auto overflow-x-hidden mb-2"
            :style     {:scrollbar-gutter "stable"}
            :on-scroll (fn [e]
                         (when (and facets-has-more?
                                    (not facets-loading?)
                                    selected-field-path)
                           (let [target (dg/target e)
                                 scroll-top (dg/scroll-top target)
                                 client-height (dg/client-height target)
                                 scroll-height (dg/scroll-height target)]
                             (when (>= (+ scroll-top client-height) (- scroll-height 24))
                               (rf/dispatch [::event/load-more-facets editor-id selected-field-path])))))}
      [:div {:class "tw2-flex tw2-flex-col tw2-gap-3 tw2-pr-3"}
       (for [option operator-options]
         ^{:key (str "operator-" (pr-str (:value option)))}
         [select-option-row {:editor-id           editor-id
                             :field-path          selected-field-path
                             :operator            select-op
                             :selected-selections selected-selections
                             :selected-operators  selected-operators
                             :current-selections  selected-selections
                             :multi?              false
                             :control-type        :checkbox
                             :option              option}])
       (for [option base-options]
         ^{:key (pr-str (:value option))}
         [select-option-row {:editor-id           editor-id
                             :field-path          selected-field-path
                             :operator            select-op
                             :selected-selections selected-selections
                             :selected-operators  selected-operators
                             :current-selections  selected-selections
                             :multi?              true
                             :control-type        :checkbox
                             :option              option}])]
      (when facets-loading?
        [:div {:class "py-2 tw2-pr-3 text-sm tw2-text-tertiary"}
         "Loading..."])
      (when (and (not facets-loading?) facets-has-more?)
        [:div {:class "py-2 tw2-pr-3 text-xs tw2-text-tertiary"}
         "Scroll for more"])]]))

(defn- scalar-input-panel
  [{:keys [editor-id selected-field selected-field-path operand-type current-op-state state* supports-suggestions?]}]
  (let [currency-field? (= (:field/data-type selected-field) :data.type/currency-value)
        currency-options currencies/value-options
        selected-currency (normalize-currency-value (:currency current-op-state))
        commit-key [editor-id selected-field-path]
        commit! (fn []
                  (commit-scalar! {:editor-id           editor-id
                                   :selected-field-path selected-field-path
                                   :currency-field?     currency-field?
                                   :state*              state*}))
        on-key-down (fn [e]
                      (when (commit/enter-key? (dg/event-key e))
                        (commit-now! commit-key commit!)))]
    [:div {:class "pt-1"}
     [:div {:class "h-[36px] flex gap-1"}
      (if (= operand-type :date)
        [date-picker/view
         {:class       "flex-1 h-[36px] border tw2-border-field-border-default rounded-[6px] text-[14px] leading-[20px]"
          :value       (:value current-op-state)
          :portal-root (dg/body)
          :on-change   (fn [value]
                         (swap! state* assoc selected-field-path (assoc current-op-state :value value))
                         (when selected-field-path
                           (set-single-selection! editor-id selected-field-path
                                                  {:kind     :value
                                                   :operator (:op current-op-state)
                                                   :value    value
                                                   :label    (or (date-picker/format-display-value value)
                                                                 (str value))})))}]
        [:<>
         (when currency-field?
           [:select
            {:class     "w-[84px] h-[36px] border tw2-border-field-border-default rounded-[6px] px-2 text-[14px] bg-white tw2-text-secondary"
             :value     (name selected-currency)
             :on-change (fn [e]
                          (swap! state* assoc selected-field-path (assoc current-op-state
                                                                         :currency (keyword (dg/event-target-value e))))
                          (commit-now! commit-key commit!))}
            (for [{:keys [value label]} currency-options]
              ^{:key (str "currency-" (name value))}
              [:option {:value (name value)} label])])
         (if (= operand-type :numeric)
           [:input
            {:type        "number"
             :step        "any"
             :class       "flex-1 h-[36px] border tw2-border-field-border-default rounded-[6px] px-3 text-[14px] leading-[20px] tw2-text-primary placeholder:tw2-text-field-placeholder-text focus:outline-none focus:ring-0"
             :placeholder (loc/tr :filters-editor/type-here)
             :value       (or (:value current-op-state) "")
             :on-change   (fn [e]
                            (let [value (dg/event-target-value e)]
                              (swap! state* assoc selected-field-path (assoc current-op-state :value value))
                              (commit-after-pause! commit-key commit!)))
             :on-blur     (fn [_e]
                            (commit-now! commit-key commit!))
             :on-key-down on-key-down}]
           [simple-text-input/view
            {:class       "flex-1 h-[36px] border tw2-border-field-border-default rounded-[6px] text-[14px] leading-[20px]"
             :placeholder (loc/tr :filters-editor/type-here)
             :value       (or (:value current-op-state) "")
             :on-change   (fn [value]
                            (swap! state* assoc selected-field-path (assoc current-op-state :value value))
                            (when (and (= operand-type :text)
                                       supports-suggestions?
                                       selected-field-path)
                              (rf/dispatch [::event/set-option-search-text editor-id selected-field-path value])))
             :on-blur     (fn [_value]
                            (commit-now! commit-key commit!))
             :on-key-down on-key-down}])])]]))

(defn- between-input-panel
  [{:keys [editor-id selected-field selected-field-path operand-type current-op-state state* date-range-modal*]}]
  (let [currency-field? (= (:field/data-type selected-field) :data.type/currency-value)
        currency-options currencies/value-options
        selected-currency (normalize-currency-value (:currency current-op-state))
        commit-key [editor-id selected-field-path]
        commit! (fn []
                  (commit-between! {:editor-id           editor-id
                                    :selected-field-path selected-field-path
                                    :state*              state*}))
        on-key-down (fn [e]
                      (when (commit/enter-key? (dg/event-key e))
                        (commit-now! commit-key commit!)))]
    [:div {:class "pt-1 flex gap-1"}
     (when (and currency-field? (not= operand-type :date))
       [:select
        {:class     "w-[84px] h-[36px] border tw2-border-field-border-default rounded-[6px] px-2 text-[14px] bg-white tw2-text-secondary"
         :value     (name selected-currency)
         :on-change (fn [e]
                      (swap! state* assoc selected-field-path (assoc current-op-state
                                                                     :currency (keyword (dg/event-target-value e))))
                      (commit-now! commit-key commit!))}
        (for [{:keys [value label]} currency-options]
          ^{:key (str "between-currency-" (name value))}
          [:option {:value (name value)} label])])
     (if (= operand-type :date)
       [:div
        {:class "h-[36px] min-w-0 flex-1 border tw2-border-field-border-default rounded-[6px] px-3 text-[14px] leading-[20px] flex items-center tw2-text-primary"}
        (if (and (:from current-op-state) (:to current-op-state))
          (str (date-picker/format-display-value (:from current-op-state))
               " .. "
               (date-picker/format-display-value (:to current-op-state)))
          (loc/tr :filters-editor/operator-between))]
       (if (= operand-type :numeric)
         [:input
          {:type        "number"
           :step        "any"
           :class       "h-[36px] border tw2-border-field-border-default rounded-[6px] px-3 text-[14px] leading-[20px] tw2-text-primary placeholder:tw2-text-field-placeholder-text focus:outline-none focus:ring-0"
           :placeholder (loc/tr :from)
           :value       (or (:from current-op-state) "")
           :on-change   (fn [e]
                          (let [value (dg/event-target-value e)]
                            (swap! state* assoc selected-field-path (assoc current-op-state :from value))
                            (commit-after-pause! commit-key commit!)))
           :on-blur     (fn [_e]
                          (commit-now! commit-key commit!))
           :on-key-down on-key-down}]
         [simple-text-input/view
          {:class       "h-[36px] border tw2-border-field-border-default rounded-[6px] text-[14px]"
           :placeholder (loc/tr :from)
           :value       (or (:from current-op-state) "")
           :on-change   (fn [value]
                          (swap! state* assoc selected-field-path (assoc current-op-state :from value)))
           :on-blur     (fn [_value]
                          (commit-now! commit-key commit!))
           :on-key-down on-key-down}]))
     (if (= operand-type :date)
       [button/secondary-hollow
        {:class    "h-[36px] px-3 rounded-[6px] text-[13px] leading-[20px] whitespace-nowrap"
         :on-click (fn [_e]
                     (rf/dispatch [::event/set-field-picker-open editor-id false])
                     (reset! date-range-modal* {:open?      true
                                                :field-path selected-field-path}))}
        (loc/tr :filters-editor/operator-between)]
       (if (= operand-type :numeric)
         [:input
          {:type        "number"
           :step        "any"
           :class       "h-[36px] border tw2-border-field-border-default rounded-[6px] px-3 text-[14px] leading-[20px] tw2-text-primary placeholder:tw2-text-field-placeholder-text focus:outline-none focus:ring-0"
           :placeholder (loc/tr :to)
           :value       (or (:to current-op-state) "")
           :on-change   (fn [e]
                          (let [value (dg/event-target-value e)]
                            (swap! state* assoc selected-field-path (assoc current-op-state :to value))
                            (commit-after-pause! commit-key commit!)))
           :on-blur     (fn [_e]
                          (commit-now! commit-key commit!))
           :on-key-down on-key-down}]
         [simple-text-input/view
          {:class       "h-[36px] border tw2-border-field-border-default rounded-[6px] text-[14px]"
           :placeholder (loc/tr :to)
           :value       (or (:to current-op-state) "")
           :on-change   (fn [value]
                          (swap! state* assoc selected-field-path (assoc current-op-state :to value)))
           :on-blur     (fn [_value]
                          (commit-now! commit-key commit!))
           :on-key-down on-key-down}]))]))

(defn- operator-panel
  [{:keys [editor-id selected-field selected-field-path operand-type current-op-state state* date-range-modal* facet-options selected-selections facets-loading?]}]
  (let [supports-suggestions? (supports-text-suggestions? selected-field)
        text-suggestion-options (->> (or facet-options [])
                                     (remove (fn [{:keys [value]}]
                                               (or (= value known-option-value)
                                                   (= value unknown-option-value))))
                                     vec)]
    [:<>
     (for [{:keys [op label preset count from to]} (if (= operand-type :date)
                                                     (date-operator-options selected-field-path selected-field facet-options)
                                                     (operator-options selected-field operand-type facet-options))]
       (let [row-key (str (name op) "-" (or (some-> preset name) "default"))
             row-id (str "simple-filter-operator-" (name op) "-" (or (some-> preset name) "default"))
             selected-row? (operator-row-selected? current-op-state {:op op :preset preset})]
         ^{:key row-key}
         [:div {:class "space-y-1"}
          [operator-row/view
           {:selected?    selected-row?
            :id           row-id
            :control-type :radio
            :label        (if (some? count)
                            (str label " (" count ")")
                            label)
            :on-click     (fn [_e]
                            (if selected-row?
                              (do
                                (clear-local-op-state! state* selected-field-path)
                                (reset! date-range-modal* {:open? false
                                                           :field-path nil})
                                (rf/dispatch [::event/clear-field-filter editor-id selected-field-path]))
                              (do
                                (let [existing-selection (first (or selected-selections []))
                                      existing-date-value (selection->scalar-date-value op existing-selection)
                                      typed-date-value (parse-dd-mm-yyyy->utc-ts (visible-date-input-value))
                                      resolved-date-value (or (:value current-op-state)
                                                              existing-date-value
                                                              typed-date-value)
                                      next-op-state (cond-> (assoc current-op-state :op op :preset preset)
                                                      (and (= operand-type :date)
                                                           (date-scalar-op? op)
                                                           (some? resolved-date-value))
                                                      (assoc :value resolved-date-value))]
                                  (swap! state* assoc selected-field-path next-op-state)
                                ;; Switching a numeric operator should immediately refresh results
                                ;; when the panel already holds the value or the two bounds.
                                  (when (and (= operand-type :numeric)
                                             (nil? preset))
                                    (when-let [selection (commit/operator-switch-selection
                                                          (cond-> next-op-state
                                                            (contains? commit/scalar-numeric-operators op)
                                                            (update :currency normalize-currency-value))
                                                          (= (:field/data-type selected-field)
                                                             :data.type/currency-value))]
                                      (set-single-selection! editor-id selected-field-path selection)))
                                ;; Switching date operators should immediately refresh results
                                ;; when a date value already exists.
                                  (when (and (= operand-type :date)
                                             (date-scalar-op? op)
                                             (some? (:value next-op-state)))
                                    (set-single-selection! editor-id selected-field-path
                                                           {:kind     :value
                                                            :operator op
                                                            :value    (:value next-op-state)
                                                            :label    (or (date-picker/format-display-value (:value next-op-state))
                                                                          (str (:value next-op-state)))})))
                                (when (and (= operand-type :date)
                                           (= preset date-preset-custom))
                                  (rf/dispatch [::event/set-field-picker-open editor-id false])
                                  (reset! date-range-modal* {:open?      true
                                                             :field-path selected-field-path}))
                                (when (and (= operand-type :date)
                                           (keyword? preset)
                                           (not= preset date-preset-custom))
                                  (apply-date-preset! editor-id selected-field-path {:preset preset
                                                                                     :label label
                                                                                     :from from
                                                                                     :to to}))
                                (when (and (= operand-type :numeric)
                                           (numeric-preset-option? {:value preset}))
                                  (apply-numeric-preset! editor-id selected-field-path {:preset preset
                                                                                        :label label
                                                                                        :from from
                                                                                        :to to}))
                                (when (= op op-known)
                                  (rf/dispatch [::event/clear-option-search-text editor-id selected-field-path])
                                  (set-single-selection! editor-id selected-field-path {:kind     :value
                                                                                        :operator op-known
                                                                                        :value    known-option-value
                                                                                        :label    (loc/tr :filters-editor/is-known)}))
                                (when (= op op-unknown)
                                  (rf/dispatch [::event/clear-option-search-text editor-id selected-field-path])
                                  (set-single-selection! editor-id selected-field-path {:kind     :value
                                                                                        :operator op-unknown
                                                                                        :value    unknown-option-value
                                                                                        :label    (loc/tr :filters-editor/is-unknown)}))
                                (when (= op op-true)
                                  (rf/dispatch [::event/clear-option-search-text editor-id selected-field-path])
                                  (set-single-selection! editor-id selected-field-path {:kind     :value
                                                                                        :operator op-true
                                                                                        :value    true
                                                                                        :label    (loc/tr :filters-editor/is-true)}))
                                (when (= op op-false)
                                  (rf/dispatch [::event/clear-option-search-text editor-id selected-field-path])
                                  (set-single-selection! editor-id selected-field-path {:kind     :value
                                                                                        :operator op-false
                                                                                        :value    false
                                                                                        :label    (loc/tr :filters-editor/is-false)}))
                                (when (= op op-contains)
                                  (rf/dispatch [::event/clear-field-filter editor-id selected-field-path])))))}]
          (when (and (nil? preset)
                     selected-row?
                     (#{op-eq op-lt op-gt op-date-eq op-date-lt op-date-gt op-contains} op))
            [scalar-input-panel
             {:editor-id           editor-id
              :selected-field      selected-field
              :selected-field-path selected-field-path
              :operand-type        operand-type
              :current-op-state    current-op-state
              :state*              state*
              :supports-suggestions? supports-suggestions?}])
          (when (and (= operand-type :text)
                     supports-suggestions?
                     selected-row?
                     (= op op-contains))
            [:div {:class "pl-1"}
             (if (seq text-suggestion-options)
               [:div {:class "tw2-flex tw2-flex-col tw2-gap-2"}
                (for [option text-suggestion-options]
                  ^{:key (str "text-suggestion-" (pr-str (:value option)))}
                  [select-option-row {:editor-id           editor-id
                                      :field-path          selected-field-path
                                      :operator            op-contains
                                      :selected-selections selected-selections
                                      :selected-operators  #{(:op current-op-state)}
                                      :current-selections  selected-selections
                                      :multi?              true
                                      :control-type        :checkbox
                                      :on-select           (fn [selected-option _selection]
                                                             (let [selected-label (or (:label selected-option)
                                                                                      (some-> (:value selected-option) str)
                                                                                      "")]
                                                               (swap! state* assoc selected-field-path (assoc current-op-state :value selected-label))
                                                               (rf/dispatch [::event/set-option-search-text editor-id selected-field-path selected-label])))
                                      :option              option}])]
               (when facets-loading?
                 [:div {:class "py-1 text-xs tw2-text-tertiary"} "Loading..."]))])
          (when (and selected-row?
                     (#{op-between op-date-between} op)
                     (or (not= operand-type :date)
                         (= (:preset current-op-state) date-preset-custom)))
            [between-input-panel
             {:editor-id           editor-id
              :selected-field      selected-field
              :selected-field-path selected-field-path
              :operand-type        operand-type
              :current-op-state    current-op-state
              :state*              state*
              :date-range-modal*   date-range-modal*}])]))]))

(defn view
  [{:keys [editor-id init-args initialize? destroy-on-unmount?]
    :or   {initialize?         true
           destroy-on-unmount? true}}]
  (let [state* (r/atom {})
        date-range-modal* (r/atom {:open?      false
                                   :field-path nil})
        field-search-focused?* (r/atom false)
        field-search-dropdown-direction* (r/atom :down)
        field-search-input-ref* (r/atom nil)]
    (r/create-class
     {:display-name "FiltersEditor"
      :component-did-mount
      (fn []
        (js/console.log "[simple-filters] component-did-mount"
                        #js {:editorId            editor-id
                             :initialize          initialize?
                             :hasInitArgs         (some? init-args)
                             :initLoadFacetsEvent (pr-str (:load-facets-event init-args))})
        (when initialize?
          (rf/dispatch [::event/initialize
                        (merge {:editor-id editor-id}
                               init-args)])))
      :component-will-unmount
      (fn []
        (when destroy-on-unmount?
          (rf/dispatch [::event/destroy editor-id])))
      :reagent-render
      (fn [_]
        (let [runtime @(rf/subscribe [::sub/runtime editor-id])
              section-items @(rf/subscribe [::sub/section-items editor-id])
              active-selection-count @(rf/subscribe [::sub/active-selection-count editor-id])
              field-picker-open? @(rf/subscribe [::sub/field-picker-open? editor-id])
              field-picker-search-text @(rf/subscribe [::sub/field-picker-search-text editor-id])
              open-field-path @(rf/subscribe [::sub/open-field-path editor-id])
              filters-by-field-path @(rf/subscribe [::sub/filters-by-field-path editor-id])
              fields (get runtime model/fields-key {})
              field-paths (:ordered-paths fields)
              field-by-path (:by-path fields)
              section-groups (->> section-items
                                  (map (fn [{:keys [section-key field-paths]}]
                                         {:section-key section-key
                                          :field-paths (->> field-paths distinct vec)}))
                                  (filter (comp seq :field-paths))
                                  vec)
              visible-field-paths (->> section-groups (mapcat :field-paths) vec)
              selected-field-path (or open-field-path (first visible-field-paths))
              selected-field (get field-by-path selected-field-path)
              selected-filter (get filters-by-field-path selected-field-path)
              operand-type (field-operand-type selected-field)
              option-search-text @(rf/subscribe [::sub/option-search-text editor-id selected-field-path])
              facet-options @(rf/subscribe [::sub/field-facet-options editor-id selected-field-path])
              facets-loading? @(rf/subscribe [::sub/field-facets-loading? editor-id selected-field-path])
              facets-has-more? @(rf/subscribe [::sub/field-facets-has-more? editor-id selected-field-path])
              all-visible-paths (set @(rf/subscribe [::sub/all-visible-field-paths editor-id]))
              all-addable-paths (->> field-paths
                                     (remove (fn [field-path]
                                               (contains? all-visible-paths field-path)))
                                     vec)
              available-to-add (->> all-addable-paths
                                    (filter (fn [field-path]
                                              (let [q (string/lower-case (or field-picker-search-text ""))]
                                                (or (string/blank? q)
                                                    (-> (field-label field-path (get field-by-path field-path))
                                                        string/lower-case
                                                        (string/includes? q))))))
                                    vec)
              default-op-state (let [base (derive-op-state selected-filter)
                                     no-selection? (empty? (:selections selected-filter))]
                                 (if (and (= operand-type :text)
                                          no-selection?)
                                   (assoc base :op op-contains)
                                   base))
              current-op-state (or (get @state* selected-field-path) default-op-state)
              modal-field-path (:field-path @date-range-modal*)
              modal-filter (get filters-by-field-path modal-field-path)
              modal-default-op-state (derive-op-state modal-filter)
              modal-op-state (or (get @state* modal-field-path) modal-default-op-state)]
          (when (and selected-field-path
                     (not (contains? @state* selected-field-path)))
            (swap! state* assoc selected-field-path default-op-state))
          (when (and modal-field-path
                     (not (contains? @state* modal-field-path)))
            (swap! state* assoc modal-field-path modal-default-op-state))
          [:div
           {:class "tw2"}
           [:div
            {:class "flex items-center gap-4"}
            [popover/view
             {:open?          (boolean field-picker-open?)
              :on-open-change (fn [open?]
                                (js/console.log "[simple-filters] popover open-change"
                                                #js {:editorId               editor-id
                                                     :open                   (boolean open?)
                                                     :openFieldPath          (pr-str open-field-path)
                                                     :visibleFieldPathsCount (count visible-field-paths)})
                                (rf/dispatch [::event/set-field-picker-open editor-id open?])
                                (when-not open?
                                  (reset! field-search-focused?* false)
                                  (reset! field-search-dropdown-direction* :down))
                                (when (and open?
                                           (nil? open-field-path)
                                           (seq visible-field-paths))
                                  (rf/dispatch [::event/open-field-path editor-id (first visible-field-paths)])))
              :as-child?      true}
             [button/secondary-open
              {:class "w-[132px] h-[44px] px-4 rounded-[8px] text-[14px] leading-[20px] font-medium tw2-text-primary flex items-center justify-center gap-[6px]"}
              (loc/tr :filter-by)
              (when (pos? active-selection-count)
                [:span
                 {:class "inline-flex items-center justify-center w-[20px] h-[20px] px-[6px] rounded-[6px] bg-success-light text-success-light-text text-[12px] leading-[16px] font-medium"}
                 active-selection-count])
              [:i.fal.fa-angle-down]]

             [grid-layout/view-2x2
              {:class                  "tw2 tw2-w-[680px] tw2-max-w-[95vw] shadow-sm overflow-visible tw2-rounded-filterbox transition-all duration-200 ease-out origin-top-left opacity-100 translate-y-0 scale-100 tw2-bg-[var(--filterbox-bg)] border border-solid"
               :style                  {:border-color "var(--filterbox-border)"}
               :height-class           ""
               :max-height-class       "max-h-[80vh]"
               :grid-cols-class        "grid-cols-[minmax(200px,260px)_minmax(280px,420px)]"
               :top-left-class         "border-r border-solid"
               :top-left-style         {:border-color "var(--filterbox-border)"}
               :top-left-scroll-class  "overflow-hidden"
               :top-right-scroll-class "overflow-hidden"
               :bottom-left-class      "border-r border-t border-solid"
               :bottom-right-class     "border-t border-solid"
               :bottom-left-style      {:border-color "var(--filterbox-border)"}
               :bottom-right-style     {:border-color "var(--filterbox-border)"}}
              [left-top-panel
               {:editor-id             editor-id
                :section-groups        section-groups
                :field-by-path         field-by-path
                :selected-field-path   selected-field-path
                :filters-by-field-path filters-by-field-path}]
              [right-top-panel
               {:editor-id           editor-id
                :selected-field      selected-field
                :selected-field-path selected-field-path
                :selected-selections (:selections selected-filter)
                :option-search-text  option-search-text
                :current-op-state    current-op-state
                :operand-type        operand-type
                :state*              state*
                :facet-options       facet-options
                :facets-loading?     facets-loading?
                :facets-has-more?    facets-has-more?
                :date-range-modal*   date-range-modal*}]
              [left-bottom-panel
               {:editor-id                editor-id
                :field-picker-search-text field-picker-search-text
                :available-to-add         available-to-add
                :field-by-path            field-by-path
                :selected-field-path      selected-field-path
                :field-search-focused?*   field-search-focused?*
                :field-search-dropdown-direction* field-search-dropdown-direction*
                :field-search-input-ref*  field-search-input-ref*}]
              [right-bottom-panel
               {:editor-id           editor-id
                :selected-field-path selected-field-path
                :state*              state*}]]]
            (when field-picker-open?
              [button/secondary-hollow
               {:class    "h-[44px] px-4 rounded-[8px] text-[14px] leading-[20px] font-medium tw2-text-primary"
                :on-mouse-down (fn [e]
                                 (dg/prevent-and-stop e)
                                 (reset! state* {})
                                 (reset! date-range-modal* {:open? false
                                                            :field-path nil})
                                 (rf/dispatch [::event/clear-all-filters editor-id]))
                :on-click (fn [e]
                            (dg/prevent-and-stop e)
                            (reset! state* {})
                            (reset! date-range-modal* {:open? false
                                                       :field-path nil})
                            (rf/dispatch [::event/clear-all-filters editor-id]))}
               (loc/tr :clear-all)])
            (when (and (:open? @date-range-modal*)
                       modal-field-path)
              [date-range-picker/view
               {:visible?  true
                :value     {:start-ts (:from modal-op-state)
                            :end-ts   (:to modal-op-state)}
                :on-close  (fn []
                             (reset! date-range-modal* {:open?      false
                                                        :field-path nil}))
                :on-change (fn [{:keys [start-ts end-ts]}]
                             (when (and (nil? start-ts)
                                        (nil? end-ts))
                               (swap! state* assoc modal-field-path (assoc modal-op-state
                                                                           :from nil
                                                                           :to nil))
                               (rf/dispatch [::event/clear-field-filter editor-id modal-field-path])))
                :on-apply  (fn [{:keys [start-ts end-ts]}]
                             (let [next-state (assoc modal-op-state
                                                     :from start-ts
                                                     :to end-ts)]
                               (swap! state* assoc modal-field-path next-state)
                               (set-single-selection! editor-id modal-field-path
                                                      {:kind     :value
                                                       :operator (:op modal-op-state)
                                                       :from     start-ts
                                                       :to       end-ts
                                                       :currency (:currency next-state)
                                                       :value    (str (date-picker/format-display-value start-ts)
                                                                      " .. "
                                                                      (date-picker/format-display-value end-ts))
                                                       :label    (str (date-picker/format-display-value start-ts)
                                                                      " .. "
                                                                      (date-picker/format-display-value end-ts))}))
                             (reset! date-range-modal* {:open?      false
                                                        :field-path nil}))}])]]))})))
